import React, { useEffect } from 'react'
import cx from 'classnames'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { l } from '@manychat/manyui'

import { EngagementGoalEditor } from 'apps/aiAutomation/components/EngagementGoalEditor/EngagementGoalEditor'
import { GoalTypePicker } from 'apps/aiAutomation/components/GoalTypePicker'
import { InstantAnswers } from 'apps/aiAutomation/components/InstantAnswers/InstantAnswers'
import { KnowledgeAdded } from 'apps/aiAutomation/components/KnowledgeAdded'
import { KnowledgeEditor } from 'apps/aiAutomation/components/KnowledgeEditor/KnowledgeEditor'
import { KnowledgeEntryTypePicker } from 'apps/aiAutomation/components/KnowledgeEntryTypePicker'
import { Content } from 'apps/aiAutomation/components/Layout/components/Content/Content'
import { Sidebar } from 'apps/aiAutomation/components/Layout/components/Sidebar/Sidebar'
import { SidebarView } from 'apps/aiAutomation/components/Layout/components/SidebarView/SidebarView'
import { Layout } from 'apps/aiAutomation/components/Layout/Layout'
import { PositiveCommentReplies } from 'apps/aiAutomation/components/PositiveCommentReplies/PositiveCommentReplies'
import { TovSetup } from 'apps/aiAutomation/components/TovSetup/TovSetup'
import { NavigationLink } from 'apps/aiAutomation/layout/components/NavigationLink/NavigationLink'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import {
  AiAlwaysOnAutomationsActions,
  AiAlwaysOnAutomationsSelectors,
} from 'apps/aiAutomation/store/core'
import { LoaderStatus as ConfigLoaderStatus } from 'apps/aiAutomation/store/core/lib/types'
import { AiAutomationEngagementGoalActions } from 'apps/aiAutomation/store/engagementGoal'
import {
  AiAutomationKnowledgeActions,
  AiAutomationKnowledgeSelectors,
} from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutSelectors } from 'apps/aiAutomation/store/layout'
import { ErrorBoundary } from 'common/core/components/ErrorBoundary'
import Page from 'common/core/components/Page/Page'
import { typedRouting } from 'utils/router'

import cm from './PageLayout.module.css'

interface PageLayoutProps {
  children: React.ReactNode
}

export const PageLayout = ({ children }: PageLayoutProps) => {
  const dispatch = useAppDispatch()

  const isSidebarOpen = useAppSelector(AiAutomationLayoutSelectors.getIsSidebarOpen)
  const isKnowledgeListLoaded = useAppSelector(AiAutomationKnowledgeSelectors.getIsListLoaded)
  const configLoaderStatus = useAppSelector(AiAlwaysOnAutomationsSelectors.getLoaderStatus)

  useEffect(() => {
    if (configLoaderStatus === ConfigLoaderStatus.NOT_LOADED) {
      dispatch(AiAlwaysOnAutomationsActions.getConfigFx())
    }
  }, [dispatch, configLoaderStatus, isKnowledgeListLoaded])

  useEffect(() => {
    if (!isKnowledgeListLoaded) {
      dispatch(AiAutomationKnowledgeActions.getListFx())
    }
  }, [dispatch, isKnowledgeListLoaded])

  useEffect(() => {
    dispatch(AiAutomationEngagementGoalActions.getListFx())
  }, [dispatch])

  return (
    <ErrorBoundary scope="ai" section="ai_always_on_automation">
      <Page title="Manychat AI" contentClassName={cm.page} hideMobileWarning>
        <Layout>
          <div className={cm.pageLayout}>
            <nav
              aria-label={l.translate('Manychat AI navigation')}
              className={cx(cm.nav, isSidebarOpen && cm.nav_muted)}
            >
              <ul className="m-0 p-0">
                <li>
                  <NavigationLink
                    to={typedRouting.linkBuilder.aiOverview()}
                    label={l.translate('Overview')}
                    icon="Analytics"
                  />
                </li>

                <li>
                  <NavigationLink
                    to={typedRouting.linkBuilder.aiTraining()}
                    label={l.translate('Training')}
                    icon="AiKnowledge"
                  />
                </li>

                <li>
                  <NavigationLink
                    to={typedRouting.linkBuilder.aiSkills()}
                    label={l.translate('Skills')}
                    icon="Publish"
                  />
                </li>
              </ul>
            </nav>

            <Content>{children}</Content>
          </div>

          <Sidebar>
            <SidebarView viewId={SidebarViewType.KNOWLEDGE_EDITOR}>
              <KnowledgeEditor />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.INSTANT_ANSWERS}>
              <InstantAnswers />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.POSITIVE_COMMENT_REPLIES}>
              <PositiveCommentReplies />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.TOV_SETUP}>
              <TovSetup />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.KNOWLEDGE_ADDED}>
              <KnowledgeAdded />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.ENGAGEMENT_GOAL_EDITOR}>
              <EngagementGoalEditor />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.GOAL_TYPE_PICKER}>
              <GoalTypePicker />
            </SidebarView>

            <SidebarView viewId={SidebarViewType.KNOWLEDGE_ENTRY_TYPE_PICKER}>
              <KnowledgeEntryTypePicker />
            </SidebarView>
          </Sidebar>
        </Layout>
      </Page>
    </ErrorBoundary>
  )
}
